import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import Database from '../../../types/Database'

type DisputeStatus = Database.DisputeStatus
type DiveStatus = Database.Dive['status']

const DISPUTE_STATUS_COLORS: Record<
  DisputeStatus,
  'warning' | 'info' | 'success' | 'default'
> = {
  open: 'warning',
  under_review: 'info',
  resolved: 'success',
  dismissed: 'default'
}

const DIVE_STATUS_COLORS: Record<
  DiveStatus,
  'success' | 'info' | 'warning' | 'error' | 'default'
> = {
  open: 'success',
  full: 'info',
  cancelled: 'warning',
  completed: 'default',
  removed: 'error'
}

interface DiveDetail {
  id: string
  siteName: string
  hostId: string
  description: string
  locationDisplay: string
  dateTimeDisplay: string
  status: DiveStatus
  price: number | null
  maxParticipants: number
  type: 'buddy' | 'guided'
  duration: number
  depthTier: string
  moderationReason: string | null
}

interface DisputeRow {
  id: string
  diveId: string
  diveName: string
  disputerId: string
  disputerName: string
  disputerEmail: string
  reason: string
  details: string
  status: DisputeStatus
  createdAt: Date
  resolvedAt: Date | null
  resolutionNotes: string | null
}

const formatAUD = (amount: number | null): string => {
  if (amount === null) return '—'
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount)
}

const DiveDisputesTab = () => {
  const [disputes, setDisputes] = useState<DisputeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Resolve dialog state
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [selectedDispute, setSelectedDispute] = useState<DisputeRow | null>(
    null
  )
  const [resolveComment, setResolveComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Dive detail modal state
  const [diveDetailOpen, setDiveDetailOpen] = useState(false)
  const [diveDetailLoading, setDiveDetailLoading] = useState(false)
  const [selectedDiveDetail, setSelectedDiveDetail] =
    useState<DiveDetail | null>(null)
  const [selectedDiveHostName, setSelectedDiveHostName] = useState('')

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchDisputes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const db = getFirestore()
      const disputesRef = collection(db, 'disputes')
      const q = query(disputesRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      const userCache = new Map<
        string,
        { email: string; displayName: string }
      >()
      const diveCache = new Map<string, string>()

      const resolveUser = async (
        uid: string
      ): Promise<{ email: string; displayName: string }> => {
        if (userCache.has(uid)) return userCache.get(uid)!
        try {
          const userDoc = await getDoc(doc(db, 'users', uid))
          if (userDoc.exists()) {
            const data = userDoc.data()
            const result = {
              email: (data.email as string) || '—',
              displayName: (data.displayName as string) || uid
            }
            userCache.set(uid, result)
            return result
          }
        } catch {
          // fallback
        }
        const fallback = { email: '—', displayName: uid }
        userCache.set(uid, fallback)
        return fallback
      }

      const resolveDiveName = async (diveId: string): Promise<string> => {
        if (diveCache.has(diveId)) return diveCache.get(diveId)!
        try {
          const diveDoc = await getDoc(doc(db, 'dives', diveId))
          if (diveDoc.exists()) {
            const name = (diveDoc.data().siteName as string) || diveId
            diveCache.set(diveId, name)
            return name
          }
        } catch {
          // fallback
        }
        diveCache.set(diveId, diveId)
        return diveId
      }

      const rows: DisputeRow[] = await Promise.all(
        snapshot.docs.map(async d => {
          const data = d.data()
          const user = await resolveUser(data.disputerId as string)
          const diveName = await resolveDiveName(data.diveId as string)
          return {
            id: d.id,
            diveId: data.diveId as string,
            diveName,
            disputerId: data.disputerId as string,
            disputerName: user.displayName,
            disputerEmail: user.email,
            reason: data.reason as string,
            details: data.details as string,
            status: data.status as DisputeStatus,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            resolvedAt: data.resolvedAt?.toDate?.() ?? null,
            resolutionNotes: (data.resolutionNotes as string | null) ?? null
          }
        })
      )

      setDisputes(rows)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load disputes'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDisputes()
  }, [fetchDisputes])

  const handleDiveClick = async (dispute: DisputeRow) => {
    setDiveDetailOpen(true)
    setDiveDetailLoading(true)
    setSelectedDiveHostName('')

    try {
      const db = getFirestore()
      const diveDoc = await getDoc(doc(db, 'dives', dispute.diveId))
      if (diveDoc.exists()) {
        const data = diveDoc.data()
        const location = data.location
        const dateTime = data.dateTime

        // Resolve host name
        let hostName = data.hostId as string
        try {
          const hostDoc = await getDoc(doc(db, 'users', data.hostId as string))
          if (hostDoc.exists()) {
            hostName = hostDoc.data().displayName || hostName
          }
        } catch {
          // keep UID
        }
        setSelectedDiveHostName(hostName)

        setSelectedDiveDetail({
          id: dispute.diveId,
          siteName: (data.siteName as string) || dispute.diveId,
          hostId: data.hostId as string,
          description: data.description as string,
          locationDisplay: location
            ? `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
            : '—',
          dateTimeDisplay: dateTime?.toDate?.()
            ? dateTime.toDate().toLocaleString()
            : '—',
          status: data.status as DiveStatus,
          price: (data.price as number | null) ?? null,
          maxParticipants: data.maxParticipants as number,
          type: data.type as 'buddy' | 'guided',
          duration: data.duration as number,
          depthTier: data.depthTier as string,
          moderationReason: (data.moderationReason as string | null) ?? null
        })
      } else {
        setSelectedDiveDetail(null)
      }
    } catch {
      setSelectedDiveDetail(null)
    } finally {
      setDiveDetailLoading(false)
    }
  }

  const handleOpenResolve = (dispute: DisputeRow) => {
    setSelectedDispute(dispute)
    setResolveComment('')
    setResolveDialogOpen(true)
  }

  const handleCloseResolve = () => {
    setResolveDialogOpen(false)
    setSelectedDispute(null)
    setResolveComment('')
  }

  const handleResolve = async () => {
    if (!selectedDispute) return
    if (resolveComment.length < 1 || resolveComment.length > 1000) return

    setSubmitting(true)
    try {
      const db = getFirestore()
      const auth = getAuth()
      const disputeRef = doc(db, 'disputes', selectedDispute.id)
      await updateDoc(disputeRef, {
        status: 'resolved' as DisputeStatus,
        resolvedAt: Timestamp.now(),
        resolvedBy: auth.currentUser?.uid ?? null,
        resolutionNotes: resolveComment.trim()
      })

      setSnackbar({
        open: true,
        message: 'Dispute marked as resolved.',
        severity: 'success'
      })
      handleCloseResolve()
      await fetchDisputes()
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve dispute'
      setSnackbar({ open: true, message, severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant='outlined' onClick={fetchDisputes}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Dive Disputes
      </Typography>

      {disputes.length === 0 ? (
        <Typography color='text.secondary'>No disputes found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Dive</TableCell>
                <TableCell>Disputer</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {disputes.map(dispute => (
                <TableRow key={dispute.id}>
                  <TableCell>
                    {dispute.createdAt.toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Link
                      component='button'
                      variant='body2'
                      onClick={() => handleDiveClick(dispute)}
                      sx={{ cursor: 'pointer', textAlign: 'left' }}
                    >
                      {dispute.diveName}
                    </Link>
                  </TableCell>
                  <TableCell>{dispute.disputerName}</TableCell>
                  <TableCell>{dispute.disputerEmail}</TableCell>
                  <TableCell>{dispute.reason}</TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {dispute.details}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={dispute.status.replace('_', ' ')}
                      color={
                        DISPUTE_STATUS_COLORS[dispute.status] ?? 'default'
                      }
                      size='small'
                    />
                  </TableCell>
                  <TableCell>
                    {dispute.status !== 'resolved' &&
                    dispute.status !== 'dismissed' ? (
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => handleOpenResolve(dispute)}
                      >
                        Resolve
                      </Button>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        {dispute.resolvedAt
                          ? `Resolved ${dispute.resolvedAt.toLocaleDateString()}`
                          : 'Resolved'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleCloseResolve}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Resolve Dispute</DialogTitle>
        <DialogContent>
          {selectedDispute && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                <strong>Disputer:</strong> {selectedDispute.disputerName} (
                {selectedDispute.disputerEmail})
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Dive:</strong> {selectedDispute.diveName}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Reason:</strong> {selectedDispute.reason}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                <strong>Details:</strong> {selectedDispute.details}
              </Typography>
            </Box>
          )}

          <TextField
            label='Resolution Comment'
            multiline
            rows={4}
            fullWidth
            required
            value={resolveComment}
            onChange={e => setResolveComment(e.target.value)}
            error={resolveComment.length > 1000}
            helperText={`${resolveComment.length}/1000 characters (minimum 1)`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResolve} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            variant='contained'
            disabled={
              submitting ||
              resolveComment.length < 1 ||
              resolveComment.length > 1000
            }
          >
            {submitting ? <CircularProgress size={20} /> : 'Mark as Resolved'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dive Detail Modal */}
      <Dialog
        open={diveDetailOpen}
        onClose={() => {
          setDiveDetailOpen(false)
          setSelectedDiveDetail(null)
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Dive Details</DialogTitle>
        <DialogContent>
          {diveDetailLoading && (
            <Box display='flex' justifyContent='center' py={4}>
              <CircularProgress />
            </Box>
          )}
          {!diveDetailLoading && !selectedDiveDetail && (
            <Typography color='text.secondary'>
              Dive details not available.
            </Typography>
          )}
          {!diveDetailLoading && selectedDiveDetail && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 1.5,
                pt: 1
              }}
            >
              <Typography variant='body2'>
                <strong>Title:</strong> {selectedDiveDetail.siteName}
              </Typography>
              <Typography variant='body2'>
                <strong>Host:</strong> {selectedDiveHostName}
              </Typography>
              <Typography variant='body2'>
                <strong>Description:</strong> {selectedDiveDetail.description}
              </Typography>
              <Typography variant='body2'>
                <strong>Location:</strong> {selectedDiveDetail.locationDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Date:</strong> {selectedDiveDetail.dateTimeDisplay}
              </Typography>
              <Typography variant='body2'>
                <strong>Price:</strong>{' '}
                {selectedDiveDetail.price != null
                  ? formatAUD(selectedDiveDetail.price)
                  : 'Free'}
              </Typography>
              <Typography variant='body2'>
                <strong>Capacity:</strong> {selectedDiveDetail.maxParticipants}
              </Typography>
              <Typography variant='body2'>
                <strong>Type:</strong> {selectedDiveDetail.type}
              </Typography>
              <Typography variant='body2'>
                <strong>Duration:</strong> {selectedDiveDetail.duration} min
              </Typography>
              <Typography variant='body2'>
                <strong>Depth Tier:</strong> {selectedDiveDetail.depthTier}
              </Typography>
              <Typography variant='body2'>
                <strong>Status:</strong>{' '}
                <Chip
                  label={selectedDiveDetail.status}
                  color={
                    DIVE_STATUS_COLORS[selectedDiveDetail.status] ?? 'default'
                  }
                  size='small'
                />
              </Typography>
              {selectedDiveDetail.status === 'removed' &&
                selectedDiveDetail.moderationReason && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Alert severity='warning' sx={{ mt: 1 }}>
                      <strong>Removal reason:</strong>{' '}
                      {selectedDiveDetail.moderationReason}
                    </Alert>
                  </Box>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiveDetailOpen(false)
              setSelectedDiveDetail(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DiveDisputesTab
